import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';

import {
  removePrefixFromFilter,
  transformFollowUpData,
  transformNewlyData,
  transformPartialsData,
} from './utils';

import type {
  IFollowUpResponseData,
  IFollowUpTableData,
  IFollowUpTableDataFilters,
  INewlyResponseData,
  INewlyTableData,
  INewlyTableDataFilters,
  IPartialsResponseData,
  IPartialsTableData,
  IPartialsTableDataFilters,
  TBaseTableQueryResponse,
  TBaseTableResponse,
} from 'infrastructure/interfaces';
import type { QueryKey } from '@tanstack/react-query';

export const CCM_CONSOLE_PAGE_SIZE = 10;

export const getNewlyTableDataKey = (
  filter: INewlyTableDataFilters,
): QueryKey => ['getNewlyTableData', filter];

const newlyQueryFn = async (filter: INewlyTableDataFilters) => {
  const url = 'core-2/ccm/console/new-patients';

  const filterWithoutPrefix = removePrefixFromFilter(filter, 'n-');
  const {
    clinicGuid,
    numberOfCalls,
    page,
    excludeClinicGuid,
    patientGuid,
    ccmCaseManagerGuid,
    podFilter,
    ...otherFilter
  } = filterWithoutPrefix;

  const sort = Object.entries(otherFilter)
    .reduce<Array<[string, any]>>((acc, [key, value]) => {
      type TKey = keyof typeof otherFilter | (string & {});
      let newKey: TKey = key;
      if (key === 'dueDate') newKey = 'ccmDueDate';
      if (key === 'appointmentDate') newKey = 'nextAppointment';
      if (key === 'enrolledDate') newKey = 'enrollmentDate';
      if (key === 'excludeClinicGuid') return acc;

      acc.push([newKey, value]);
      return acc;
    }, [])
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${value}`)
    .join('');

  const { data, error } = await dataService.getList<
    TBaseTableResponse<INewlyResponseData>
  >(
    apiUrlGenerator(url, {
      clinicGuid,
      excludeClinicGuid,
      page,
      items: CCM_CONSOLE_PAGE_SIZE,
      numberOfCalls,
      patientGuid,
      ccmCaseManagerGuid,
      podFilter,
      sort,
    }),
  );

  if (error) {
    return Promise.reject(error);
  }

  const rows = transformNewlyData(data?.rows);
  const filteredRows =
    podFilter === 'outside'
      ? rows.filter((r) => r.podSection === 'outside')
      : rows;

  return {
    data: filteredRows,
    count: data?.count,
  };
};

export const useGetNewlyTableData = (filter: INewlyTableDataFilters) => {
  const { data, isLoading, isFetching, isError, error } = useQuery<
    TBaseTableQueryResponse<INewlyTableData> | undefined,
    string | undefined
  >({
    queryKey: getNewlyTableDataKey(filter),
    queryFn: async () => newlyQueryFn(filter),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { data, isLoading: isLoading || isFetching, isError };
};

// =========== FOLLOW UP TABLE ===================

export const getFollowUpTableDataKey = (
  filter: IFollowUpTableDataFilters,
): QueryKey => ['getFollowUpTableData', filter];

const followUpQueryFn = async (filter: IFollowUpTableDataFilters) => {
  const url = 'core-2/ccm/console/followup-patients';
  const filterWithoutPrefix = removePrefixFromFilter(filter, 'f-');
  const {
    clinicGuid,
    numberOfCalls,
    page,
    excludeClinicGuid,
    patientGuid,
    ccmCaseManagerGuid,
    completed,
    podFilter,
    ...otherFilter
  } = filterWithoutPrefix;

  const sort = Object.entries(otherFilter)
    .reduce<Array<[string, any]>>((acc, [key, value]) => {
      type TKey = keyof typeof otherFilter | (string & {});
      let newKey: TKey = key;
      if (key === 'dueDate') newKey = 'ccmDueDate';
      if (key === 'appointmentDate') newKey = 'nextAppointment';
      if (key === 'actionTimes') newKey = 'time';
      if (key === 'callsMonth') newKey = 'calls';

      acc.push([newKey, value]);
      return acc;
    }, [])
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${value}`)
    .join('');

  const { data, error } = await dataService.getList<
    TBaseTableResponse<IFollowUpResponseData>
  >(
    apiUrlGenerator(url, {
      sort,
      numberOfCalls,
      clinicGuid,
      excludeClinicGuid,
      patientGuid,
      completed,
      page,
      ccmCaseManagerGuid,
      podFilter,
      items: CCM_CONSOLE_PAGE_SIZE,
    }),
  );

  if (error) {
    return Promise.reject(error);
  }

  const rows = transformFollowUpData(data?.rows);
  const filteredRows =
    podFilter === 'outside'
      ? rows.filter((r) => r.podSection === 'outside')
      : rows;

  return { data: filteredRows, count: data?.count };
};

export const useGetFollowUpTableData = (filter: IFollowUpTableDataFilters) => {
  const { data, isLoading, isFetching, isError } = useQuery<
    TBaseTableQueryResponse<IFollowUpTableData> | undefined,
    string | undefined
  >({
    queryKey: getFollowUpTableDataKey(filter),
    queryFn: async () => followUpQueryFn(filter),
    keepPreviousData: true,
  });

  return { data, isLoading: isLoading || isFetching, isError };
};

// =========== PARTIALS TABLE ===================

export const getPartialsTableDataKey = (
  filter: IPartialsTableDataFilters,
): QueryKey => ['getPartialsTableData', filter];

const partialsQueryFn = async (filter: IPartialsTableDataFilters) => {
  const url = 'core-2/ccm/console/partial-patients';
  const filterWithoutPrefix = removePrefixFromFilter(filter, 'p-');
  const {
    clinicGuid,
    excludeClinicGuid,
    page,
    patientGuid,
    ccmCaseManagerGuid,
    podFilter,
    ...otherFilter
  } = filterWithoutPrefix;

  const sort = Object.entries(otherFilter)
    .reduce<Array<[string, any]>>((acc, [key, value]) => {
      type TKey = keyof typeof otherFilter | (string & {});
      let newKey: TKey = key;
      if (key === 'dueDate') newKey = 'ccmDueDate';
      if (key === 'appointmentDate') newKey = 'nextAppointment';
      if (key === 'enrolledDate') newKey = 'enrollmentDate';
      if (key === 'callsMonth') newKey = 'calls';
      if (key === 'nextBillable') newKey = 'time';
      if (key === 'minutes') newKey = 'time';
      if (key === 'excludeClinicGuid') return acc;

      acc.push([newKey, value]);
      return acc;
    }, [])
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${value}`)
    .join('');

  const { data, error } = await dataService.getList<
    TBaseTableResponse<IPartialsResponseData>
  >(
    apiUrlGenerator(url, {
      sort,
      clinicGuid,
      excludeClinicGuid,
      patientGuid,
      page,
      ccmCaseManagerGuid,
      podFilter,
      items: CCM_CONSOLE_PAGE_SIZE,
    }),
  );

  if (error) {
    return Promise.reject(error);
  }

  const rows = transformPartialsData(data?.rows);
  const filteredRows =
    podFilter === 'outside'
      ? rows.filter((r) => r.podSection === 'outside')
      : rows;

  return { data: filteredRows, count: data?.count };
};

export const useGetPartialsTableData = (filter: IPartialsTableDataFilters) => {
  const { data, isLoading, isFetching, isError } = useQuery<
    TBaseTableQueryResponse<IPartialsTableData> | undefined,
    string | undefined
  >({
    queryKey: getPartialsTableDataKey(filter),
    queryFn: async () => partialsQueryFn(filter),
    keepPreviousData: true,
  });

  return { data, isLoading: isLoading || isFetching, isError };
};
type TableMap = {
  newly: INewlyTableDataFilters;
  'follow-up': IFollowUpTableDataFilters;
  partials: IPartialsTableDataFilters;
};

export const tableDataQueryKeyMap: {
  [K in keyof TableMap]: (filter: TableMap[K]) => QueryKey;
} = {
  newly: getNewlyTableDataKey,
  'follow-up': getFollowUpTableDataKey,
  partials: getPartialsTableDataKey,
};
